import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, Trash2, Loader2, FileSearch, Columns2, Lightbulb, FileUp } from "lucide-react";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { VerdictPanel } from "@/components/VerdictPanel";
import { PendingPanel } from "@/components/PendingPanel";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { streamAnalyze, streamSuggest, streamInvoice } from "@/lib/stream";
import { useAuth } from "@/context/AuthContext";
import { handleReviewError } from "@/lib/reviewError";

const blank = () => ({ account: "", type: "debit", amount: "" });

const SAMPLE = `Dr Rent Expense A/c 60,000
Dr Input CGST A/c 5,400
Dr Input SGST A/c 5,400
Cr Bank A/c 64,800
Cr TDS Payable A/c 6,000
(Office rent paid for June with GST and TDS u/s 194-I)`;

const SITUATION_SAMPLE = `We bought a delivery van for Rs. 8,00,000 plus 28% GST from a dealer in the same state. Paid 2,00,000 by cheque and the rest is on a 3-year bank loan. Insurance of 25,000 and registration of 40,000 were also paid.`;

export default function Analyze() {
  const { user, entitlement, refreshEntitlement } = useAuth();
  const navigate = useNavigate();
  const isGuest = !user;
  const [mode, setMode] = useState("form");
  const [framework, setFramework] = useState("indas");
  const [depth, setDepth] = useState("quick");
  const [partial, setPartial] = useState(null);
  const [stage, setStage] = useState(null);
  const [narration, setNarration] = useState("");
  const [rawText, setRawText] = useState("");
  const [situation, setSituation] = useState("");
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [lines, setLines] = useState([{ account: "", type: "debit", amount: "" }, { account: "", type: "credit", amount: "" }]);
  const [loading, setLoading] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [result, setResult] = useState(null);
  const [compare, setCompare] = useState(null);

  const busy = loading || comparing;
  const update = (i, k, v) => setLines(lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));

  const buildPayload = () => ({
    mode,
    framework,
    depth,
    narration,
    raw_text: rawText,
    lines: mode === "form"
      ? lines.filter((l) => l.account.trim() && l.amount !== "").map((l) => ({ account: l.account.trim(), type: l.type, amount: Number(l.amount) }))
      : [],
  });

  const validate = (payload) => {
    if (mode === "form" && payload.lines.length === 0) { toast.error("Add at least one entry line with an account and amount."); return false; }
    if (mode === "text" && !rawText.trim()) { toast.error("Paste your journal entry first."); return false; }
    return true;
  };

  const runStream = async (fn, arg, successMsg) => {
    setLoading(true);
    setResult(null);
    setCompare(null);
    setPartial(null);
    setStage(null);
    try {
      let failed = null;
      await fn(arg, {
        onStage: (s) => setStage(s.label),
        onPartial: (p) => setPartial(p),
        onDone: (doc) => setResult(doc),
        onError: (e) => { failed = e?.detail || "Something went wrong. Please try again."; },
      });
      if (failed) toast.error(failed);
      else toast.success(successMsg);
      refreshEntitlement();
    } catch (e) {
      handleReviewError(e, navigate, isGuest);
    } finally {
      setLoading(false);
      setPartial(null);
      setStage(null);
    }
  };

  const submit = async () => {
    if (mode === "invoice") {
      if (!invoiceFile) return toast.error("Choose the invoice PDF first.");
      const fd = new FormData();
      fd.append("file", invoiceFile);
      fd.append("framework", framework);
      return runStream(streamInvoice, fd, "Entry drafted from the invoice");
    }
    if (mode === "situation") {
      if (situation.trim().length < 10) return toast.error("Describe the situation in a little more detail.");
      return runStream(streamSuggest, { situation, framework }, "Entry drafted");
    }
    const payload = buildPayload();
    if (!validate(payload)) return;
    return runStream(streamAnalyze, payload, "Analysis ready");
  };

  const pollCompare = async (jobId) => {
    for (let i = 0; i < 80; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      let job;
      try {
        ({ data: job } = await api.get(`/analyze/compare/${jobId}`));
      } catch {
        toast.error("Lost track of the comparison. Please try again.");
        refreshEntitlement();
        return;
      }
      if (job.results && Object.keys(job.results).length) setCompare(job.results);
      if (job.status === "done") {
        toast.success("Both frameworks reviewed");
        refreshEntitlement();
        return;
      }
      if (job.status === "error" || job.status === "partial") {
        toast.error(job.error || "Comparison failed. Your reviews were credited back.");
        refreshEntitlement();
        return;
      }
    }
    toast.error("The comparison is taking unusually long. Please try again.");
    refreshEntitlement();
  };

  const runCompare = async () => {
    const payload = buildPayload();
    if (!validate(payload)) return;
    setComparing(true);
    setResult(null);
    setCompare(null);
    try {
      const { data: job } = await api.post("/analyze/compare", payload);
      refreshEntitlement();
      toast.message("Reviewing under Ind AS and AS — a few seconds.");
      await pollCompare(job.job_id);
    } catch (e) {
      handleReviewError(e, navigate, isGuest);
    } finally {
      setComparing(false);
    }
  };

  return (
    <div className="grain min-h-screen">
      <Nav />
      <main className="mx-auto max-w-[1400px] px-6 py-14 lg:px-12">
        <p className="overline text-slate-500">Journal entry review</p>
        <h1 className="font-display mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Entry Analyzer</h1>
        {isGuest ? (
          <p className="mt-4 font-mono-data text-xs text-slate-500" data-testid="signin-note">
            {entitlement ? `${entitlement.reviews_left} of 50 free reviews left` : "50 free reviews"} · no sign-in needed ·{" "}
            <Link to="/login" className="underline hover:text-[#0f172a]">create a free account</Link> for 100 more.
          </p>
        ) : entitlement && !entitlement.unlimited && entitlement.reviews_left <= 5 ? (
          <p className="mt-4 font-mono-data text-xs text-slate-500" data-testid="low-balance-note">
            {entitlement.reviews_left} review(s) left — <Link to="/pricing" className="underline hover:text-[#0f172a]">top up</Link>.
          </p>
        ) : null}

        <div className="mt-14 grid gap-10 lg:grid-cols-[1.05fr_1fr]">
          {/* Input column */}
          <section className="border border-stone-200 bg-white">
            <div className="border-b border-stone-200 p-6">
              <p className="overline mb-3 text-slate-500">Framework</p>
              <div className="flex border border-stone-300">
                {[["indas", "Ind AS"], ["as", "Indian GAAP (AS)"]].map(([k, label]) => (
                  <button key={k} onClick={() => setFramework(k)} disabled={busy} data-testid={`framework-${k}`}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors duration-150 disabled:opacity-60 ${framework === k ? "bg-[#0f172a] text-white" : "bg-white text-slate-600 hover:bg-stone-50"}`}>
                    {label}
                  </button>
                ))}
              </div>
              {(mode === "form" || mode === "text") && (
                <>
                  <p className="overline mb-3 mt-6 text-slate-500">Review speed</p>
                  <div className="flex border border-stone-300">
                    {[["quick", "Quick check", "~4s"], ["deep", "Deep review", "~7s"]].map(([k, label, hint]) => (
                      <button key={k} onClick={() => setDepth(k)} disabled={busy} data-testid={`depth-${k}`}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-colors duration-150 disabled:opacity-60 ${depth === k ? "bg-[#0f172a] text-white" : "bg-white text-slate-600 hover:bg-stone-50"}`}>
                        {label} <span className="font-mono-data text-xs opacity-70">{hint}</span>
                      </button>
                    ))}
                  </div>
                  <p className="mt-3 font-mono-data text-[11px] text-slate-400">
                    {depth === "quick"
                      ? "Fast reviewer — right for everyday entries. Costs the same, and the verdict writes itself out as it thinks."
                      : "Slower, more thorough reviewer for tricky recognition and measurement calls. Compare Ind AS vs AS always uses this one."}
                  </p>
                </>
              )}
            </div>
            <div className="border-b border-stone-200 px-6 pt-6">
              <div className="flex gap-6">
                {[["form", "Structured lines"], ["text", "Paste text"], ["situation", "Describe a situation"], ["invoice", "Upload invoice"]].map(([k, label]) => (
                  <button key={k} onClick={() => setMode(k)} disabled={busy} data-testid={`mode-${k}`}
                    className={`overline pb-3 transition-colors duration-150 disabled:opacity-60 ${mode === k ? "border-b-2 border-[#0f172a] text-[#0f172a]" : "border-b-2 border-transparent text-slate-400 hover:text-slate-600"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6">
              {mode === "form" ? (
                <div className="space-y-3" data-testid="entry-lines">
                  {lines.map((l, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <select value={l.type} onChange={(e) => update(i, "type", e.target.value)} data-testid={`line-type-${i}`}
                        className="w-20 border border-stone-300 bg-white px-2 py-2 font-mono-data text-sm focus:outline-none focus:ring-2 focus:ring-stone-900">
                        <option value="debit">Dr</option>
                        <option value="credit">Cr</option>
                      </select>
                      <Input value={l.account} onChange={(e) => update(i, "account", e.target.value)} placeholder="Ledger account" data-testid={`line-account-${i}`}
                        className="rounded-none border-stone-300 focus-visible:ring-stone-900" />
                      <Input value={l.amount} onChange={(e) => update(i, "amount", e.target.value)} placeholder="Amount" type="number" data-testid={`line-amount-${i}`}
                        className="w-32 rounded-none border-stone-300 text-right font-mono-data focus-visible:ring-stone-900" />
                      <button onClick={() => setLines(lines.filter((_, x) => x !== i))} data-testid={`line-remove-${i}`}
                        className="p-2 text-slate-400 transition-colors duration-150 hover:text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button onClick={() => setLines([...lines, blank()])} data-testid="add-line-btn"
                    className="mt-2 inline-flex items-center gap-1.5 border border-stone-300 px-3 py-2 text-xs text-slate-600 transition-colors duration-150 hover:border-[#0f172a] hover:text-[#0f172a]">
                    <Plus className="h-3.5 w-3.5" /> Add line
                  </button>
                </div>
              ) : mode === "text" ? (
                <div>
                  <Textarea value={rawText} onChange={(e) => setRawText(e.target.value)} rows={9} data-testid="raw-text-input"
                    placeholder={"Dr Machinery A/c 5,00,000\nCr Bank A/c 5,00,000"}
                    className="rounded-none border-stone-300 font-mono-data text-sm focus-visible:ring-stone-900" />
                  <button onClick={() => setRawText(SAMPLE)} data-testid="load-sample-btn"
                    className="mt-3 font-mono-data text-xs text-slate-500 underline transition-colors duration-150 hover:text-[#0f172a]">
                    load a sample entry
                  </button>
                </div>
              ) : mode === "situation" ? (
                <div>
                  <p className="mb-3 text-sm leading-relaxed text-slate-600">
                    Not sure what the entry should be? Describe what happened in plain English and we'll draft it.
                  </p>
                  <Textarea value={situation} onChange={(e) => setSituation(e.target.value)} rows={8} data-testid="situation-input"
                    placeholder={"We sold goods worth 2,00,000 to a customer in Maharashtra on credit, with 18% GST. Our own state is Maharashtra."}
                    className="rounded-none border-stone-300 text-sm focus-visible:ring-stone-900" />
                  <button onClick={() => setSituation(SITUATION_SAMPLE)} data-testid="load-situation-sample-btn"
                    className="mt-3 font-mono-data text-xs text-slate-500 underline transition-colors duration-150 hover:text-[#0f172a]">
                    load a sample situation
                  </button>
                </div>
              ) : (
                <div>
                  <p className="mb-4 text-sm leading-relaxed text-slate-600">
                    Upload a purchase or sales invoice and we'll read the party, GST split and totals, then draft the entry to book it.
                  </p>
                  <label className="flex cursor-pointer flex-col items-center justify-center border border-dashed border-stone-300 p-10 text-center transition-colors duration-150 hover:border-[#0f172a]">
                    <FileUp className="h-5 w-5 text-slate-400" />
                    <span className="mt-4 font-mono-data text-xs text-slate-600">{invoiceFile ? invoiceFile.name : "click to choose a .pdf invoice"}</span>
                    <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="invoice-file-input"
                      onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)} />
                  </label>
                  <p className="mt-4 font-mono-data text-[11px] leading-relaxed text-slate-400">
                    Text-based PDFs only (max 8 MB). Scans and photos have no readable text — type those into "Describe a situation".
                  </p>
                </div>
              )}

              {mode !== "situation" && mode !== "invoice" && (
                <div className="mt-6">
                  <p className="overline mb-2 text-slate-500">Narration (optional)</p>
                  <Input value={narration} onChange={(e) => setNarration(e.target.value)} placeholder="Being machinery purchased for cash" data-testid="narration-input"
                    className="rounded-none border-stone-300 focus-visible:ring-stone-900" />
                </div>
              )}

              <button onClick={submit} disabled={busy} data-testid="analyze-btn"
                className="mt-8 flex w-full items-center justify-center gap-2 bg-[#0f172a] px-6 py-4 text-sm font-medium text-white transition-colors duration-150 hover:bg-stone-800 disabled:opacity-60">
                {loading
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> {mode === "form" || mode === "text" ? "Reviewing entry…" : mode === "invoice" ? "Reading the invoice…" : "Drafting the entry…"}</>
                  : mode === "situation" ? <><Lightbulb className="h-4 w-4" /> Suggest the entry</>
                  : mode === "invoice" ? <><FileUp className="h-4 w-4" /> Draft entry from invoice</>
                  : <>Analyze entry</>}
              </button>

              {mode !== "situation" && mode !== "invoice" && (
                <button onClick={runCompare} disabled={busy} data-testid="compare-btn"
                  className="mt-3 flex w-full items-center justify-center gap-2 border border-[#0f172a] px-6 py-4 text-sm font-medium text-[#0f172a] transition-colors duration-150 hover:bg-stone-100 disabled:opacity-60">
                  {comparing ? <><Loader2 className="h-4 w-4 animate-spin" /> Reviewing under both…</> : <><Columns2 className="h-4 w-4" /> Compare Ind AS vs AS</>}
                </button>
              )}
            </div>
          </section>

          {/* Verdict column */}
          <section className="lg:sticky lg:top-24 lg:self-start">
            {result ? <VerdictPanel result={result} /> : loading ? (
              <PendingPanel
                lines={mode === "form" ? buildPayload().lines : []}
                label={stage || (mode === "invoice" ? "Reading the invoice" : mode === "situation" ? "Drafting the entry" : "Reviewing entry")}
                partial={partial}
              />
            ) : (
              <div className="flex flex-col items-center justify-center border border-dashed border-stone-300 bg-white p-16 text-center" data-testid="verdict-empty">
                <FileSearch className="h-6 w-6 text-slate-300" />
                <p className="font-display mt-6 text-lg font-bold tracking-tight">
                  {compare ? "Comparison below" : mode === "form" || mode === "text" ? "No verdict yet" : "No entry drafted yet"}
                </p>
                <p className="mt-2 max-w-xs text-sm leading-relaxed text-slate-500">
                  {compare
                    ? "Both framework verdicts are shown side by side underneath."
                    : mode === "situation"
                      ? "Describe the transaction on the left and the drafted entry will appear here."
                      : mode === "invoice"
                        ? "Upload an invoice on the left and the entry to book it will appear here."
                        : "Enter the debit and credit legs, pick your framework, and the review will appear here."}
                </p>
              </div>
            )}
          </section>
        </div>

        {(compare || comparing) && (
          <section className="mt-16" data-testid="compare-view">
            <p className="overline text-slate-500">Side by side</p>
            <h2 className="font-display mt-3 text-2xl font-bold tracking-tight md:text-lg">The same entry under both frameworks</h2>
            <div className="mt-8 grid gap-8 lg:grid-cols-2">
              {[["indas", "Ind AS"], ["as", "Indian GAAP (AS)"]].map(([k, label]) => (
                <div key={k} data-testid={`compare-${k}`}>
                  <p className="overline mb-3 bg-[#0f172a] px-3 py-2 text-white">{label}</p>
                  {compare?.[k] ? <VerdictPanel result={compare[k]} /> : (
                    <PendingPanel lines={buildPayload().lines} label={`Reviewing under ${label}`} />
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
